/**
 * What a pass that failed, stopped or waited says about itself — the sentences,
 * built once, from the error's TYPE and the record's own counts.
 *
 * The provider's own account of what went wrong is the adapter's sentence
 * (`classify` in each adapter), and it rides on the op as `error`. What the
 * adapter cannot know is what the pass had already put on the graph when it
 * failed, and that is the half a reader most needs: a stream cut after two
 * moves leaves two moves standing, and a toast that said only "the connection
 * dropped" left the reader to work out whether the pass had done anything. So
 * every surface that reports a failed pass — the toast, the feed's message, the
 * run strip's ending — joins the two here, and none of them writes the clause
 * again.
 *
 * Decided on the kind, never on the English (invariant 7's rule, applied to the
 * words as well as to the run): the switches below are exhaustive over
 * `ModelError['kind']` with no `default`, so a new kind stops the build here too
 * until somebody writes what it means to a reader.
 *
 * No React, no DOM, and nothing at runtime but the status table, the model
 * registry a declined pass reads its alternatives from — and one timer, the
 * quiet watch's, which decides when the quiet line is said.
 */

import type { ModelError, RetryWait, WaitCause } from '../engine/providers/contract'
import { ROLE_LABEL, answeredElsewhere, type Role } from './models'
import type { ModelStamp, Run } from './types'
import { WAIT_WORD } from './vocabulary'

type Kind = ModelError['kind']

/** "The 2 nodes that arrived before it stay on the graph." — or nothing, when
 *  nothing had landed. The nodes are the record's own count, never a guess. */
export function landedClause(n: number): string {
  if (n <= 0) return ''
  return `The ${n === 1 ? 'node' : `${n} nodes`} that arrived before it ${n === 1 ? 'stays' : 'stay'} on the graph.`
}

/** A failed pass's toast: the adapter's sentence, then what stayed. */
export function failureSentence(message: string, landed: number): string {
  const tail = landedClause(landed)
  return tail ? `${message} ${tail}` : message
}

/** A refused pass's toast. A refusal can arrive after part of the answer — the
 *  nodes before it landed and stay, and a bare "declined" over them read as a
 *  pass that produced nothing. */
export function refusalSentence(note: string, landed: number): string {
  if (landed <= 0) return note
  return `${note} The ${landed === 1 ? 'node' : `${landed} nodes`} it wrote before declining ${landed === 1 ? 'stays' : 'stay'} on the graph.`
}

/**
 * The provider's refusal categories, in plain words (D312). The set is OPEN on
 * the provider's side, so a category not listed here is printed as it came,
 * underscores read as spaces, rather than dropped or guessed at.
 */
const SAFEGUARD_TOPIC: Readonly<Record<string, string>> = {
  bio: 'biology',
  cyber: 'cybersecurity',
  reasoning_extraction: 'revealing its reasoning',
  frontier_llm: 'AI model development',
  general_harms: 'general harms',
}

export const safeguardTopic = (category: string): string =>
  SAFEGUARD_TOPIC[category] ?? category.replace(/_/g, ' ')

/** Names as a reader says them: "A", "A and B", "A, B and C". */
const listed = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

/**
 * What a declined pass says (D312).
 *
 * On Anthropic a refusal is not the model talking itself out of an answer: the
 * `refusal` stop reason is the provider's safety classifiers turning the request
 * away, with the filter's category where the provider names one. A bare "The
 * model declined" left the reader to guess whether the question, the model or
 * the app was at fault — and on the default model, whose filter reaches into
 * biology Opus 5 answered (D109), the fix can be another model. So an
 * Anthropic refusal says it was the filter, names the category, and names the
 * rows recorded as answering what this one declines (`answeredElsewhere`), with
 * where to route one: the reader chooses, and nothing is re-sent to another
 * model on their behalf (invariant 3).
 *
 * Another provider's refusal is the model's own words, and those are the whole
 * message, as they always were, with the caller's sentence where there are none.
 */
export function declinedMessage(
  fallback: string,
  declined: { refusalNote?: string; refusalCategory?: string },
  model: Pick<ModelStamp, 'provider' | 'id' | 'name'> | undefined,
  /** The route the declined pass ran on, so the sentence can name the Settings
   *  row that re-points it. Absent where the caller cannot say — a feed row read
   *  back later, or a pass handed its model directly, as the blind half of an
   *  idea opened twice is handed the other provider's — and then no row is
   *  named, because naming the wrong one sends the reader to a control that
   *  moves some other pass. */
  role?: Role,
): string {
  if (model?.provider !== 'anthropic') return declined.refusalNote || fallback
  const category = declined.refusalCategory
  const topic = category ? safeguardTopic(category) : null
  const lead = `Anthropic's safety filter declined this pass${topic ? ` (${topic})` : ''}.`
  const others = answeredElsewhere(model.id, category)
  const route = role
    ? ` To try again, set ${ROLE_LABEL[role]} under Settings → Routing to ${others.length === 1 ? 'it' : 'one of them'}.`
    : ''
  const next = others.length
    ? ` Filters differ by model: ${listed(others.map((m) => m.name))} ${others.length === 1 ? 'answers' : 'answer'} some ${topic} questions ${model.name} declines.${route}`
    : ' Filters differ by model, so another model may answer it.'
  const note = declined.refusalNote ? ` Anthropic's note: ${declined.refusalNote}` : ''
  return `${lead}${next}${note}`
}

/**
 * The feed's note under a pass that ended on an error.
 *
 * It used to open "Stopped partway:" whatever had happened and close "The trace
 * below is what arrived." whether or not anything had — so a rate limit that
 * turned the request away before a byte came back read as a pass cut off
 * halfway, over an empty trace. Now it says which: the reader's own Stop, or the
 * provider's sentence, and then what the pass left — nodes, a trace, or nothing.
 */
export function passFailureNote(
  op: { error?: string; errorKind?: Kind },
  landed: number,
  traced: boolean,
): string {
  const arrived = landed > 0 || traced
  const head =
    op.errorKind === 'aborted'
      ? arrived
        ? 'You stopped it partway.'
        : 'You stopped it before anything arrived.'
      : (op.error ?? '')
  const tail =
    landed > 0
      ? `${landedClause(landed)} ${landed === 1 ? 'It is' : 'They are'} listed below.`
      : traced
        ? 'The trace below is what arrived.'
        : op.errorKind === 'aborted'
          ? ''
          : 'Nothing had arrived.'
  return tail ? `${head} ${tail}` : head
}

/**
 * The line a pass carries while the adapter waits to ask again — in the action
 * slot under its trace (`searching`), because it is something the transport is
 * doing, not something the model thought. It used to say nothing at all: the
 * live pass read "Reading the branch from the root down…" through two waits the
 * API had asked for, up to a minute each, over a request nobody was reading.
 */
export function waitLine(w: RetryWait): string {
  const sec = Math.max(1, Math.ceil(w.ms / 1000))
  return w.ms > 0
    ? `${WAIT_WORD[w.cause]} — asking again in ${sec} s (try ${w.attempt} of ${w.of}). Nothing is billed while it waits.`
    : `${WAIT_WORD[w.cause]} — asking again (try ${w.attempt} of ${w.of}).`
}

/** How long a live stream may hear nothing before it says so. */
export const QUIET_MS = 60_000

/** "Nothing new from the API for 2 min" — the head every quiet line shares, in
 *  whole minutes, so a reader who has met it on one surface knows it on another. */
const quietFor = (ms: number): string =>
  `Nothing new from the API for ${Math.max(1, Math.floor(ms / 60_000))} min`

/** A live pass that has heard nothing for a while — short, because it stands on
 *  the action line under the trace, which is one clipped row. An Ask turn says the
 *  same, under its answer so far: its Stop keeps that too. */
export function quietLine(ms: number): string {
  return `${quietFor(ms)}. Stop keeps what has arrived.`
}

/**
 * …on the panel's line, naming the seats that have gone quiet: the line follows
 * one seat's thought, and the one that stalled is often not it — seat one can be
 * streaming while seat two, started beside it, has sent nothing since it opened.
 * `seats` count from 1, in lens order, as the thought above names them ("Seat 2
 * of 3 — …"); the count is left to it, because this is the same one clipped row
 * and the seat is the part that must survive the clip.
 */
export function seatsQuietLine(seats: readonly number[], ms: number): string {
  const which =
    seats.length === 1
      ? `seat ${seats[0]}`
      : `seats ${seats.slice(0, -1).join(', ')} and ${seats.at(-1)}`
  return `${quietFor(ms)} on ${which}. Stop keeps what has arrived.`
}

/**
 * …and under the voice, whose Stop sits beside this line. Not "Stop keeps what
 * has arrived": a stopped narration's words stay on screen for the sitting but
 * are not kept as a narration (`narrationStopped` says why), so the line says
 * what Stop does to it and what it cannot touch — the pass above.
 */
export function narrationQuietLine(ms: number): string {
  return `${quietFor(ms)}. Stop ends it; the pass above is unaffected.`
}

/** What a quiet watch hears from the stream it watches. */
export interface QuietWatch {
  /** Something arrived: the quiet, if it was said, is over. */
  heard: () => void
  /** The transport is waiting to ask again (`on`), which says so itself, or has
   *  just asked: the quiet is not counted through a wait, and starts over after. */
  waiting: (on: boolean) => void
  /** The request has settled: the watch ends, and unsays itself if it spoke. */
  stop: () => void
}

/**
 * Watch one live request for quiet, and say how long it has been.
 *
 * A stream that stalls — the socket open, no bytes — is bounded only by the
 * transport's ten-minute deadline (`PASS_TIMEOUT_MS`), and until then a live pass
 * looked exactly like one thinking hard: the same trace, the same caret. Measured
 * in the app with a mock that held the socket open, nothing changed on screen for
 * as long as it was held. So once `QUIET_MS` passes with nothing heard, `say`
 * hears how long it has been — again every few seconds while it lasts — and
 * `null` the moment anything arrives, or as the watch stops after it spoke.
 *
 * Never a stop condition and never a retry: a long quiet is not proof of a hang,
 * and the deadline stays the only thing that ends one. One watch per request,
 * and the sentence is the caller's (`quietLine`, `seatsQuietLine`,
 * `narrationQuietLine`), because what Stop can do differs by where it is read.
 * It was a lone pass's alone (D218); an Ask turn, the narration and each of the
 * panel's seats run the same one, on the same threshold.
 */
export function watchQuiet(say: (ms: number | null) => void): QuietWatch {
  let last = Date.now()
  let shown = false
  let paused = false
  const timer = setInterval(() => {
    const gap = Date.now() - last
    if (paused || gap < QUIET_MS) return
    shown = true
    say(gap)
  }, 5_000)
  return {
    heard() {
      last = Date.now()
      if (!shown) return
      shown = false
      say(null)
    },
    waiting(on) {
      paused = on
      last = Date.now()
      // The wait's own line takes the place; a quiet said before it is over.
      if (!on || !shown) return
      shown = false
      say(null)
    },
    stop() {
      clearInterval(timer)
      if (!shown) return
      shown = false
      say(null)
    },
  }
}

/** Which of the wait words a failed pass's kind reads as, for a wait that is
 *  the RUN's between two moves — or null for a kind a run does not wait on. */
export function pauseCause(kind: Kind | undefined): WaitCause | null {
  switch (kind) {
    case 'rate':
      return 'rate'
    case 'server':
      return 'server'
    case 'network':
      return 'network'
    case 'auth':
    case 'quota':
    case 'storage':
    case 'request':
    case 'aborted':
    case 'timeout':
    case 'unknown':
    case undefined:
      return null
  }
}

/** The run strip's line while a run waits between moves after a failed one —
 *  short, because it stands where the move in flight's name stands, after the
 *  ordinal of the move it waits to make: "Move 3 of 12 · Rate limited · in 29 s". */
export function runPauseLine(pause: { cause: WaitCause; msLeft: number }): string {
  const sec = Math.max(1, Math.ceil(pause.msLeft / 1000))
  return `${WAIT_WORD[pause.cause]} · in ${sec} s`
}

/** …and as the strip's live region says it: once, with no seconds in it. The
 *  line above counts down a second at a time, and inside the run's one polite
 *  region every tick was an announcement — thirty of them for one rate limit,
 *  the flood D216 took off the streaming line. The seconds are for the eye. */
export function runPauseSpoken(pause: { cause: WaitCause }): string {
  return `${WAIT_WORD[pause.cause]} · waiting before the next move`
}

/** …and in full, in the strip's details. */
export function runPauseWhy(pause: { cause: WaitCause; msLeft: number }): string {
  return `The last move failed (${WAIT_WORD[pause.cause].toLowerCase()}), so the run waits before the next rather than walking into the same wall. Nothing is billed while it waits, and Stop ends it now.`
}

/**
 * Why a run that ended as `unreachable` stopped, in its last failed pass's own
 * sentence — or null where it did not end that way, or ended with no failed pass
 * to quote (a key taken away between moves).
 *
 * "The model stopped answering" is true of all of them and useful for none: an
 * empty account and a rate limit end the run under the same head, and only one
 * of them is fixed by waiting. The pass's note is already the record's, on the
 * move row the account prints; the strip's ending now says it too, where the
 * reader who came back to a stopped run is looking — directly over Continue,
 * which picks the run up where its failed passes left it (D337). The sentence is
 * what tells the reader whether pressing it now would only fail again.
 */
export function unreachableWhy(run: Pick<Run, 'stop' | 'moves'> | undefined): string | null {
  if (run?.stop !== 'unreachable') return null
  return run.moves.findLast((m) => m.outcome === 'error' && m.note)?.note ?? null
}

/**
 * What the voice says when its own call fails.
 *
 * The narration is not a press — it follows the pass that authorised it — so
 * the adapter's "wait a minute and try again" was advice about a button that
 * does not exist. It says instead that there is no narration this time, why in
 * a few words, and that the pass it would have described is untouched.
 */
export function narrationFailed(kind: Kind): string {
  const why = ((): string => {
    switch (kind) {
      case 'rate':
        return 'the API was rate limiting'
      case 'server':
        return 'the API had trouble on its side'
      case 'network':
        return 'the connection failed'
      case 'timeout':
        return 'the API did not answer in time'
      case 'auth':
        return 'the key was rejected'
      case 'quota':
        return 'the account has no credits left'
      case 'storage':
        return 'this browser could not save the last pass'
      case 'request':
      case 'aborted':
      case 'unknown':
        return 'the request failed'
    }
  })()
  return `No narration this time: ${why}. The pass above is unaffected.`
}

/**
 * What the voice says when the reader stopped it — the one way a narration now
 * ends as `aborted`, since the deadline's abort is reclassified as `timeout`.
 *
 * Said under whatever had arrived, which stays on screen and carries no chips,
 * and is not stored: its receipt records a narration that did not finish, and
 * a stored voice turn has no field for "stopped", so keeping the words would
 * replay half a narration as a whole one after a reload. Either way the pass it
 * followed is untouched — Stop aborts the narration's own request and nothing
 * else (D51).
 */
export function narrationStopped(arrived: boolean): string {
  return `${arrived ? 'You stopped the narration partway.' : 'You stopped the narration before it said anything.'} The pass above is unaffected.`
}
